import { Box, Button, MUIDataTableColumnDef, Pill, PillVariant, Typography } from 'web-library';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';


import { makeStyles } from 'tss-react/mui';
import { TableState } from 'web-library/stories/organism/DataTable/DataTable';
import RouteKey from '../../../../routes/key';

import {
  ApplicationStatus,
  CandidateRegisteredExamListingExamLevel,
} from '../../../../api/candidate/exam/model';
import DeleteButton from '../../../../components/buttons/DeleteButton';
import { stringToLongDateAndShortTime } from '../../../../utils/DateUtils';

const { withdrawal } = RouteKey.child;
const { withdrawal: withdrawalRoute, summary, reason } = RouteKey.withdrawal.child;

const useStyles = makeStyles()((theme) =>
({
  examLevelCol: {
    '&:not(:last-child)': {
      marginBottom: theme.spacing(3),
    },
  },
  actionButton: {
    margin: 'auto',
    paddingLeft: 0,
    minWidth: 0,
    '& span': {
      fontSize: '18px !important',
    },
  },
  submittedBy: {
    color: theme.color.text.textTertiary,
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'row',
    width: 'max-content',
    alignItems: 'center',
  },
  emptyReserve: {
    height: 314,
  },
  emptyContainer: {
    width: '100%',
    position: 'absolute',
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 77,
    paddingBottom: 77,
  },
  lastUpdatedText: {
    color: theme.color.text.textTertiary,
    marginTop: theme.spacing(2),
  },
}));

function getCellProps(width: string) {
  return () => ({
    style: { width },
  });
}

const TableColumnDefinition = (
  tableState: TableState,
  setOpenDeleteModal: any,
  setDeleteData: any
) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();

  const handleOpenDeleteModal = (uuid: string) => {
    setDeleteData(uuid);
    setOpenDeleteModal(true);
  };

  const tableColumns: MUIDataTableColumnDef[] = [
    {
      name: 'applicationID',
      label: 'Application ID',
      options: {
        setCellProps: getCellProps('15%'),
      },
    },
    {
      name: 'examLevels',
      label: 'Exam Level(s)',
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          if (dataIndex === undefined) {
            return null;
          }

          if (!tableState.data[dataIndex].examLevels.length) {
            return '-';
          }

          return (
            <>
              {tableState.data[dataIndex].examLevels.map(
                (examLevel: CandidateRegisteredExamListingExamLevel) => (
                  <Typography
                    key={examLevel.examYear + examLevel.examLevel}
                    className={classes.examLevelCol}
                  >{`${examLevel.examLevel} ${examLevel.examYear}`}</Typography>
                )
              )}
            </>
          );
        },
        setCellProps: getCellProps('15%'),
      },
    },
    {
      name: 'withdrawalReason',
      label: 'Withdrawal Reason',
      options: {
        customBodyRender: (value: any) => value || '-',
        setCellProps: getCellProps('20%'),
      },
    },
    {
      name: 'applicationDateTime',
      label: 'Application Date/Time',
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          if (dataIndex === undefined) {
            return null;
          }

          return (
            <>
              <Typography variant="body1">
                {stringToLongDateAndShortTime(tableState.data[dataIndex].applicationDateTime)}
              </Typography>
              <Typography variant="subtitle2" className={classes.submittedBy}>
                {`Submitted by ${tableState.data[dataIndex].submittedBy}`}
              </Typography>
            </>
          );
        },
        setCellProps: getCellProps('20%'),
      },
    },
    {
      name: 'applicationStatus',
      label: 'Status',
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          let variant: PillVariant;

          switch (tableState.data[dataIndex].applicationStatus) {
            case ApplicationStatus.DRAFT:
            case ApplicationStatus.PENDING_APPROVAL_WITHDRAW:
              variant = 'info';
              break;

            case ApplicationStatus.WITHDRAWN:
              variant = 'default';
              break;

            default:
              return null;
          }
          return (
            <>
              <Pill variant={variant} label={tableState.data[dataIndex].applicationStatus} />
              <Typography className={classes.lastUpdatedText} variant="subtitle2">
                {`${t('withdrawal->>Coordinator.Listing.LastUpdatedDateTime')}
                    ${stringToLongDateAndShortTime(
                  tableState.data[dataIndex].lastUpdatedDateTime
                ) || '-'
                  }`}
              </Typography>
            </>
          );
        },
        setCellProps: getCellProps('15%'),
      },
    },
    {
      name: 'actions',
      label: 'Action(s)',
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          if (dataIndex === undefined) {
            return null;
          }

          let buttonText = '';
          let nextPageRoute = '';
          let showDelete = false;
          let disabled = false;

          switch (tableState.data[dataIndex].applicationStatus) {
            case ApplicationStatus.DRAFT:
              buttonText = t('withdrawal->>Coordinator.Listing.ContinueApplication');
              nextPageRoute = `/${withdrawal}/${withdrawalRoute.route}/${tableState.data[dataIndex].applicationUuid}/${reason.route}`;
              showDelete = true;
              disabled = tableState.data[dataIndex].closedExamLevels;
              break;

            case ApplicationStatus.PENDING_APPROVAL_WITHDRAW:
            case ApplicationStatus.WITHDRAWN:
              buttonText = t('withdrawal->>Coordinator.Listing.ViewApplication');
              nextPageRoute = `/${withdrawal}/${withdrawalRoute.route}/${tableState.data[dataIndex].applicationUuid}/${summary.route}`;
              break;

            default:
              return null;
          }

          return (
            <Box className={classes.actionButtons}>
              <Button
                variant="text"
                id={`button-continue-${dataIndex}`}
                size="small"
                color="primary"
                className={classes.actionButton}
                onClick={() => history.push(nextPageRoute)}
                disabled={disabled}
              >
                {buttonText}
              </Button>
              {showDelete && (
                <DeleteButton
                  buttonId={dataIndex}
                  deleteHandler={() =>
                    handleOpenDeleteModal(tableState.data[dataIndex].applicationUuid)
                  }
                  label={t('withdrawal->>Coordinator.Listing.Delete')}
                />
              )}
            </Box>
          );
        },
        setCellProps: getCellProps('20%'),
      },
    },
  ];
  return tableColumns;
};

export default TableColumnDefinition;
